import type { IntlShape } from 'react-intl'
import qs from 'qs'
import formatStaticAppText from 'lib/format/formatStaticAppText'
import Places from 'constants/places'
import { buildHotelUrl } from '../TrendingDestinations/utils'
import {
  isLocalDestinationOfferAllFilter,
  LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
  LOCAL_DESTINATION_OFFER_EUROPE_DESTINATION_ID,
} from './localDestinationOfferCarouselConstants'

export type CountryDestination = {
  name: string
  destinationId: string
}

export type DestinationInput = {
  name: string
  destinationId?: string
}

export type LocalDestinationOfferCarouselFilterOption = {
  value: string
  label: string
}

export type LocalDestinationOfferCarouselViewModel = {
  destinations: Array<CountryDestination>
  destinationFilterOptions: Array<LocalDestinationOfferCarouselFilterOption>
  defaultDestinationId: string | undefined
}

type EuropeanCountryDestination = CountryDestination & { regionCode: string }

function toCountryDestination(place: App.Place, regionCode: string): EuropeanCountryDestination {
  return {
    name: place.name,
    destinationId: place.id,
    regionCode,
  }
}

/**
 * Merchandising pills are the countries with enough Flash inventory to deserve a chip.
 * IE and NL storefronts still see this module (they are in EUROPE_REGIONS) but are omitted
 * here: they do not have enough Flash hotels for a home-country chip.
 */
const EUROPEAN_COUNTRY_DESTINATIONS: Array<EuropeanCountryDestination> = [
  toCountryDestination(Places.Greece, 'GR'),
  toCountryDestination(Places.UnitedKingdom, 'GB'),
  toCountryDestination(Places.Spain, 'ES'),
  toCountryDestination(Places.Italy, 'IT'),
  toCountryDestination(Places.France, 'FR'),
  toCountryDestination(Places.Germany, 'DE'),
]

export function getCountryDestinationsWithIds(
  destinations: Array<DestinationInput>,
): Array<CountryDestination> {
  return destinations.flatMap((destination) => {
    if (!destination.destinationId) {
      return []
    }

    return [{
      name: destination.name,
      destinationId: destination.destinationId,
    }]
  })
}

/**
 * Use the approved Europe country list for pills. GB and DE lead with their local country;
 * all other storefronts retain the supplied merchandising order.
 */
export function getEuropeanCountryDestinations(regionCode?: string): Array<CountryDestination> {
  const localDestination = EUROPEAN_COUNTRY_DESTINATIONS.find(destination => destination.regionCode === regionCode)

  if (!localDestination || (regionCode !== 'GB' && regionCode !== 'DE')) {
    return EUROPEAN_COUNTRY_DESTINATIONS.map(({ regionCode: _regionCode, ...destination }) => destination)
  }

  return [
    localDestination,
    ...EUROPEAN_COUNTRY_DESTINATIONS.filter(destination => destination !== localDestination),
  ].map(({ regionCode: _regionCode, ...destination }) => destination)
}

export function mapDestinationsToFilterOptions(
  intl: IntlShape,
  destinations: Array<CountryDestination>,
): Array<LocalDestinationOfferCarouselFilterOption> {
  return destinations.map(destination => ({
    value: destination.destinationId,
    label: formatStaticAppText(intl, destination.name),
  }))
}

export function formatLocalDestinationOfferAllLabel(intl: IntlShape): string {
  return intl.formatMessage({
    id: '8k4GtkJzy5gh656EioOW',
    defaultMessage: 'All',
    description: 'Filter chip label for showing hotel offers across all eligible European countries on the homepage Europe offer carousel',
  })
}

/**
 * Prefer settled hero offer ids. Ignore placeholder `{ offerIds: [], fetching: true }` lists
 * so `??` does not treat an empty array as a successful exclusion set.
 *
 * Returns `null` while the hero is still unknown and there is no fallback, so the Europe
 * row does not paint with an empty exclusion set and then drop hero duplicates later.
 */
export function resolveLocalDestinationOfferCarouselExcludedIds(
  heroOfferList: Pick<App.OfferList, 'offerIds' | 'fetching'> | undefined,
  fallbackOfferIds: Array<string>,
): Array<string> | null {
  if (heroOfferList && !heroOfferList.fetching && heroOfferList.offerIds.length > 0) {
    return heroOfferList.offerIds
  }

  const heroStillUnknown = !heroOfferList || heroOfferList.fetching
  if (heroStillUnknown && fallbackOfferIds.length === 0) {
    return null
  }

  return fallbackOfferIds
}

function buildAllDestinationFilterOption(intl: IntlShape): LocalDestinationOfferCarouselFilterOption {
  return {
    value: LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE,
    label: formatLocalDestinationOfferAllLabel(intl),
  }
}

export function buildLocalDestinationOfferCarouselViewModel(
  intl: IntlShape,
  destinationsInput: Array<DestinationInput>,
): LocalDestinationOfferCarouselViewModel {
  const destinations = getCountryDestinationsWithIds(destinationsInput)
  const destinationFilterOptions = destinations.length > 0
    ? [buildAllDestinationFilterOption(intl), ...mapDestinationsToFilterOptions(intl, destinations)]
    : []
  const defaultDestinationId = destinations.length > 0
    ? LOCAL_DESTINATION_OFFER_ALL_FILTER_VALUE
    : undefined

  return {
    destinations,
    destinationFilterOptions,
    defaultDestinationId,
  }
}

export function buildLocalDestinationViewAllUrl(
  destinations: Array<CountryDestination>,
  selectedDestinationId: string | undefined,
  europeDestinationName: string | undefined,
  intl: IntlShape,
): string | undefined {
  if (isLocalDestinationOfferAllFilter(selectedDestinationId)) {
    if (!europeDestinationName) {
      return undefined
    }

    return `/search?${qs.stringify({
      destinationName: europeDestinationName,
      destinationId: LOCAL_DESTINATION_OFFER_EUROPE_DESTINATION_ID,
      adults: 2,
      children: 'none',
      flexibleWithDate: true,
    })}`
  }

  const destination = destinations.find(item => item.destinationId === selectedDestinationId)
  if (!destination?.destinationId) {
    return undefined
  }
  return buildHotelUrl(formatStaticAppText(intl, destination.name), destination.destinationId)
}

export function isLocalDestinationOfferFilterSelectionValid(
  selectedFilter: string | undefined,
  destinations: Array<CountryDestination>,
): boolean {
  if (!selectedFilter) {
    return false
  }

  if (isLocalDestinationOfferAllFilter(selectedFilter)) {
    return destinations.length > 0
  }

  return destinations.some(destination => destination.destinationId === selectedFilter)
}
